import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const PILLARS = [
  {
    headline: "Nothing is stored.",
    body: "Your documents are processed in memory and gone the second the download completes. No content logs, no archives, no 'just in case' copies — the deal stays yours.",
    testid: "proof-pillar-retention",
  },
  {
    headline: "One plan, every engine.",
    body: "No feature gates, no per-tool upsells. The same sixteen engines at $5 a month as at firm scale — because leverage shouldn't depend on seat math.",
    testid: "proof-pillar-plan",
  },
  {
    headline: "Seconds, not suites.",
    body: "No desktop installs, no license seats, no IT ticket. A browser tab is the entire stack — open it on deal day and the work is done before the call ends.",
    testid: "proof-pillar-speed",
  },
];

export const Proof = () => (
  <section className="py-24 md:py-32 lg:py-40 bg-white border-y border-hairline" data-testid="proof-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="lg:col-span-8"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">06 — Why teams switch</span>
          <h2 className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-2xl" data-testid="proof-title">
            Principles, not promises.
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
          className="lg:col-span-4 text-base text-fog leading-relaxed self-end"
        >
          We don't quote logos or invent testimonials. Three operating principles, stated plainly —
          each one verifiable the first time you run a file.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="proof-grid">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.headline}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
            className="bg-white border border-hairline rounded-xl p-8 hover:border-azure/40 transition-colors duration-500"
            data-testid={p.testid}
          >
            <span className="font-mono text-[10px] tracking-[0.2em] text-mist block mb-6">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-medium tracking-tight text-xl md:text-2xl text-ink mb-4">{p.headline}</h3>
            <p className="text-sm text-fog leading-relaxed">{p.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
